/**
 * =========================================================================================
 * Cashfree Checkout Page
 * =========================================================================================
 */

import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Icon from 'ubs-icons';
import UButton from 'ubs-framework/v2/UButton';
import { showToastError } from 'ubs-framework/v2/common/function';
import { getCashfreeInstance } from '../../common/cashfree-loader';
import { COLOR } from 'ubs-framework/v2/common/constant';
import UIcon from 'ubs-framework/v2/UIcon';
import AppHeader from '../../../../common/components/setting-app-header';

const SubscriptionCheckout = () => {
    const history = useHistory();
    const location = useLocation();
    const _ORG_ID = useSelector(e => e?.common?.orgId);

    const [loading, setLoading] = useState(true);
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [cashfree, setCashfree] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const session = params.get('sessionId');

        if (!session) {
            showToastError('Invalid checkout session');
            history.push(`/${_ORG_ID}/manage/subscriptions/payments`);
            return;
        }

        setSessionId(session);
        initializeCashfree();
    }, [location]);

    const initializeCashfree = async () => {
        try {
            setLoading(true);
            const cashfreeInstance = await getCashfreeInstance();

            setCashfree(cashfreeInstance);
            setSdkLoaded(true);
            setLoading(false);
        } catch (error) {
            console.error('Failed to load Cashfree SDK:', error);
            showToastError('Failed to load payment gateway. Please refresh and try again.');
            setLoading(false);
        }
    };

    const handleCheckout = async () => {
        if (!cashfree || !sessionId) {
            showToastError('Payment gateway not ready. Please try again.');
            return;
        }

        try {
            setLoading(true);

            const checkoutOptions = {
                subsSessionId: sessionId,
                redirectTarget: "_self"
            };

            const result = await cashfree.subscriptionsCheckout(checkoutOptions);
            setLoading(false);
            if (result.error) {
                showToastError(result.error.message || 'Payment failed');
                setLoading(false);
            }
        } catch (error) {
            console.error('Checkout error:', error);
            showToastError('An error occurred during checkout. Please try again.');
            setLoading(false);
        }
    };

    return (
        <React.Fragment>
            <AppHeader title="Complete Your Payment" />

            <div className="u_payment-success u_flex-align u_content-center">
                {loading ? (
                    <div className="u_text-center u_fadeIn">
                        <Icon.Preloader size={48} />
                        <p className="u_mt-15 u_color-font u_small-font">
                            Preparing your secure checkout...
                        </p>
                    </div>
                ) : (
                    <div className="u_payment-success__card">
                        <div className="u_payment-success__icon-wrap">
                            <UIcon color={COLOR?.u_green} thumb={76} icon={<Icon.Diamond size={40} fill={COLOR?.u_green} />} /> 
                        </div>

                        <div className="u_payment-success__title u_payment-success__title--success">
                            Ready to Upgrade
                        </div>

                        <p className="u_color-font u_small-font u_mb-5">
                            Complete your payment to unlock your new plan
                        </p>

                        <div className="u_payment-success__details">
                            <div className="u_payment-success__detail-row">
                                <span className="u_color-font u_small-font u_flex u_align-center">
                                    <Icon.CreditCard size={15} className="u_mr-8" />
                                    Payment Gateway
                                </span>
                                <span className="u_fw-600 u_small-font u_color-title">Cashfree</span>
                            </div>
                            <div className="u_payment-success__detail-row">
                                <span className="u_color-font u_small-font u_flex u_align-center">
                                    <Icon.Lock size={15} className="u_mr-8" />
                                    Encryption
                                </span>
                                <span className="u_fw-600 u_small-font u_color-green">256-bit SSL</span>
                            </div>
                            <div className="u_payment-success__detail-row">
                                <span className="u_color-font u_small-font u_flex u_align-center">
                                    <Icon.CheckMarkRound size={15} className="u_mr-8 u_color-blue" />
                                    Status
                                </span>
                                <span className="u_fw-600 u_small-font u_color-blue">Ready</span>
                            </div>
                        </div>

                        <div id="cashfree-container"></div>

                        <div className="u_payment-success__actions">
                            <UButton
                                variant="blue"
                                onClick={handleCheckout}
                                disabled={!sdkLoaded}
                                loading={loading}
                                type="Proceed to Payment"
                                cn="u_payment-btn"
                            />
                        </div>

                        <div className="u_payment-success__change-block">
                            <p className="u_small-font u_color-font u_flex u_align-center u_content-center">
                                <Icon.Lock size={14} className="u_mr-5" />
                                Your payment is processed securely by Cashfree Payments
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
};

export default SubscriptionCheckout;
